import type { ElementRef } from '@angular/core';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChildren,
    DestroyRef,
    inject,
    input,
    signal,
    viewChild,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { NgIcon } from '@ng-icons/core';
import { ClickOutside, QpxButton, QpxIcon } from '../../../directives';
import { QpxFilterItem } from '../filter/filter-item';
import { FILTER_LAYOUT_CONTEXT } from '../filter-layout';

type ContainerLayout = 'block' | 'inline';

/**
 * A responsive filter group component that transforms between expanded and collapsed states
 * based on container width using Tailwind container queries.
 *
 * - **Expanded**: Displays a tune icon with inline filter buttons (above breakpoint)
 * - **Collapsed**: Displays a "Filters" trigger button with dropdown menu (at/below breakpoint)
 *
 * The projected `qpx-filter-item` components own their own state; this group only manages
 * layout visibility and menu open/close behavior.
 *
 * @example
 * ```html
 * <qpx-filter-group #filterGroup="qpxFilterGroup" menuTriggerClasses="@lg:hidden!" filterListClasses="@lg:inline-flex">
 *   <qpx-filter-item
 *     [filter]="categoryFilter()"
 *     (appliedChange)="onFilterApplied($event); filterGroup.closeMenu()"
 *   />
 *   <qpx-filter-item
 *     [filter]="typeFilter()"
 *     (appliedChange)="onFilterApplied($event); filterGroup.closeMenu()"
 *   />
 * </qpx-filter-group>
 * ```
 */
@Component({
    selector: 'qpx-filter-group',
    imports: [
        MatButtonModule,
        NgIcon,
        QpxButton,
        QpxIcon,
        ClickOutside,
        MatBadgeModule,
    ],
    templateUrl: './filter-group.html',
    styleUrls: ['./filter-group.scss'],
    providers: [
        {
            provide: FILTER_LAYOUT_CONTEXT,
            useExisting: QpxFilterGroup,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'contents',
    },
    exportAs: 'qpxFilterGroup',
})
export class QpxFilterGroup {
    //#region Dependency injection
    private readonly destroyRef = inject(DestroyRef);
    //#endregion

    //#region Inputs
    /**
     * Tailwind container query classes for the trigger button.
     * Hidden above breakpoint. Applied when container is at/above this breakpoint.
     * @default '@md:hidden!'
     */
    public readonly menuTriggerClasses = input<string>('@md:hidden!');

    /**
     * Tailwind container query classes for the icon.
     * Hidden above breakpoint. Applied when container is at/above this breakpoint.
     * @default '@md:flex'
     */
    public readonly iconClasses = input<string>('@md:flex');

    /**
     * Tailwind container query classes for the filter list.
     * Shows inline above breakpoint. Applied when container is at/above this breakpoint.
     * @default '@md:inline-flex'
     */
    public readonly filterListClasses = input<string>('@md:static');

    /**
     * The label displayed on the collapsed trigger button.
     * @default 'Filters'
     */
    public readonly triggerLabel = input<string>('Filters');
    //#endregion

    //#region State
    /**
     * Whether the dropdown menu is currently open (only relevant in collapsed state).
     */
    public readonly menuOpen = signal(false);

    /**
     * Current layout mode derived from CSS container query.
     * - 'compact': Menu/dropdown layout (container below breakpoint)
     * - 'inline': Inline/flex layout (container above breakpoint)
     */
    public readonly layoutMode = signal<ContainerLayout>('block');

    /**
     * Query projected filter components to compute total active filter count.
     */
    private readonly filters = contentChildren(QpxFilterItem);

    /**
     * Total count of applied filters across all projected filter components.
     * Used to display a badge on the collapsed trigger button.
     */
    protected readonly activeCount = computed(() =>
        this.filters().reduce((sum, f) => sum + f.appliedAndSelectedCount(), 0)
    );
    //#endregion

    //#region Template references
    private readonly containerRef =
        viewChild.required<ElementRef<HTMLElement>>('container');
    //#endregion

    constructor() {
        this.setupLayoutObserver();
    }

    //#region Public methods
    /**
     * Closes the dropdown menu. Call this from parent after a filter is applied
     * to automatically close the menu in collapsed mode.
     */
    public closeMenu(): void {
        this.menuOpen.set(false);
    }
    //#endregion

    //#region Template methods
    /**
     * Toggles the dropdown menu open/close state.
     */
    protected toggleMenu(): void {
        this.menuOpen.update((open) => !open);
    }
    //#endregion

    //#region Private methods
    /**
     * Sets up a ResizeObserver to detect layout changes via CSS custom property.
     * Uses RAF coalescing for performance optimization.
     */
    private setupLayoutObserver(): void {
        afterNextRender({
            read: () => {
                const container = this.containerRef()?.nativeElement;
                if (!container) return;

                let rafId: number | null = null;
                let lastValue: string | null = null;

                const updateLayout = () => {
                    rafId = null;
                    const value = getComputedStyle(container)
                        .getPropertyValue('--menu-layout')
                        .trim();

                    if (value !== lastValue) {
                        lastValue = value;
                        this.layoutMode.set(
                            (value || 'inline') as ContainerLayout
                        );
                    }
                };

                const observer = new ResizeObserver(() => {
                    if (rafId === null) {
                        rafId = requestAnimationFrame(updateLayout);
                    }
                });

                observer.observe(container);
                updateLayout();

                this.destroyRef.onDestroy(() => {
                    observer.disconnect();
                    if (rafId !== null) {
                        cancelAnimationFrame(rafId);
                    }
                });
            },
        });
    }
    //#endregion
}
